/**
 * Rate utilities and fetchers for fiat/crypto reference rates.
 * Fiat: currency-api (USD per fiat). Crypto: Binance ticker/price (USDT per crypto).
 */

const FIAT_RATES_URL = 'https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/usd.json';
const BINANCE_TICKER_URL = 'https://api.binance.com/api/v3/ticker/price';

export const FALLBACK_REFERENCE_RATES: Record<string, number> = {
  EUR: 1.085,
  USD: 0.99,
  GBP: 1.27,
  RUB: 0.0108,
};

export const STABLECOINS = ['USDT', 'USDC'];
export const CRYPTO_CURRENCIES = ['USDT', 'BTC', 'ETH', 'USDC', 'BNB', 'SOL', 'XRP'];

export function isStablecoin(code: string): boolean {
  return STABLECOINS.includes(code.toUpperCase());
}

export function isCryptoCurrency(code: string): boolean {
  return CRYPTO_CURRENCIES.includes(code.toUpperCase());
}

/**
 * Format rate for form value or display (more decimals for small crypto rates).
 */
export function formatRateForForm(r: number): string {
  if (r >= 0.01) return r.toFixed(4);
  if (r >= 0.0001) return r.toFixed(6);
  return r.toFixed(8);
}

/**
 * Fetch fiat-to-USD rate (e.g. 1 EUR = X USD). Returns USD per 1 unit of fiat.
 * For "crypto per 1 fiat" (e.g. USDT per 1 EUR) we use 1/usdPerFiat when fiat !== USD.
 */
export async function fetchFiatToUsd(currency: string): Promise<number> {
  const giveCurrency = (currency || 'EUR').toUpperCase();
  const currencyKey = giveCurrency.toLowerCase();
  try {
    const response = await fetch(FIAT_RATES_URL);
    const data = (await response.json()) as { usd?: Record<string, number> };
    if (data.usd && typeof data.usd[currencyKey] === 'number') {
      const usdPerFiat = data.usd[currencyKey];
      return giveCurrency === 'USD' ? 1.0 : 1 / usdPerFiat;
    }
  } catch {
    // fall through
  }
  return FALLBACK_REFERENCE_RATES[giveCurrency] ?? FALLBACK_REFERENCE_RATES['EUR'];
}

/**
 * Fetch Binance spot price for symbol (e.g. BTCUSDT). Returns USDT per 1 unit of base asset.
 */
export async function fetchBinancePrice(symbol: string): Promise<number> {
  const res = await fetch(`${BINANCE_TICKER_URL}?symbol=${symbol}`);
  const ticker = (await res.json()) as { symbol?: string; price?: string };
  if (!res.ok || typeof ticker?.price !== 'string') {
    throw new Error('Invalid Binance response');
  }
  const price = parseFloat(ticker.price);
  if (!Number.isFinite(price) || price <= 0) {
    throw new Error('Invalid price');
  }
  return price;
}
